/**
 * Reading a position out of somebody else's spreadsheet.
 *
 * A squad list says "Gardien", "DC", "Milieu récupérateur", "مهاجم" or "CDM",
 * and none of those are the keys this product seeds. Passing the raw text
 * through leaves a player with a position the app cannot draw, cannot group and
 * cannot put on a depth chart — which looks, on screen, exactly like no position
 * at all.
 *
 * Matching runs from the most certain evidence to the least: the org's own keys,
 * then its own labels, then a table of the abbreviations coaching staffs
 * actually write. Anything still unmatched is reported rather than dropped, so
 * the coach can see which column values the app did not understand.
 */

export interface PositionOption {
  key: string;
  label: string;
  i18nKey?: string | null;
}

/** Uppercase, unaccented, punctuation-free. "Défenseur central" → "DEFENSEUR CENTRAL". */
export function normalizePosition(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[̀-ًͯ-ْٰ]/g, '')
    .normalize('NFC')
    // Arabic folding, as any Arabic search box does it: a coach writes
    // الجناح أيمن or الجناح ايمن without thinking about it, and both mean the right
    // winger. Alef forms, taa marbuta and alef maqsura all fold.
    .replace(/[\u0623\u0625\u0622\u0671]/g, '\u0627')
    .replace(/\u0629/g, '\u0647')
    .replace(/\u0649/g, '\u064a')
    .toUpperCase()
    .replace(/[^A-Z0-9؀-ۿ]+/g, ' ')
    .trim();
}

/**
 * What coaches write, mapped to what the app seeds.
 *
 * French and Arabic are here because they are launch languages; the numeric
 * shirt-position conventions are here because a Moroccan or French squad list is
 * as likely to say "6" as "MDC". Order within a key does not matter — the
 * longest match wins, so "MILIEU DEFENSIF" beats "MILIEU".
 */
const ALIASES: Record<string, string[]> = {
  GK: [
    'GK', 'G', 'GB', 'GOALKEEPER', 'KEEPER', 'GARDIEN', 'GARDIEN DE BUT', 'PORTIER',
    'حارس', 'حارس مرمى', 'حراسة المرمى', 'ح',
  ],
  RB: [
    'RB', 'DD', 'RIGHT BACK', 'ARRIERE DROIT', 'DEFENSEUR DROIT', 'LATERAL DROIT',
    'ظهير ايمن', 'ظهير يمين',
  ],
  LB: [
    'LB', 'DG', 'LEFT BACK', 'ARRIERE GAUCHE', 'DEFENSEUR GAUCHE', 'LATERAL GAUCHE',
    'ظهير ايسر', 'ظهير يسار',
  ],
  RCB: ['RCB', 'DCD', 'RIGHT CENTRE BACK', 'RIGHT CENTER BACK', 'DEFENSEUR CENTRAL DROIT', 'قلب دفاع ايمن'],
  LCB: ['LCB', 'DCG', 'LEFT CENTRE BACK', 'LEFT CENTER BACK', 'DEFENSEUR CENTRAL GAUCHE', 'قلب دفاع ايسر'],
  CB: [
    'CB', 'DC', 'CD', 'D', 'DEF', 'DEFENDER', 'CENTRE BACK', 'CENTER BACK',
    'DEFENSEUR', 'DEFENSEUR CENTRAL', 'CENTRAL', 'AXIAL',
    'مدافع', 'قلب دفاع', 'دفاع',
  ],
  RWB: ['RWB', 'PD', 'RIGHT WING BACK', 'PISTON DROIT', 'ظهير جناح ايمن'],
  LWB: ['LWB', 'PG', 'LEFT WING BACK', 'PISTON GAUCHE', 'ظهير جناح ايسر'],
  DM: [
    'DM', 'CDM', 'MDC', 'MD', 'DEFENSIVE MIDFIELDER', 'HOLDING MIDFIELDER',
    'MILIEU DEFENSIF', 'MILIEU RECUPERATEUR', 'RECUPERATEUR', 'SENTINELLE', 'PIVOT',
    'وسط مدافع', 'ارتكاز', 'محور دفاعي',
  ],
  CM: [
    'CM', 'MC', 'M', 'MID', 'MF', 'MIDFIELDER', 'CENTRAL MIDFIELDER', 'CENTRE MIDFIELDER',
    'MILIEU', 'MILIEU CENTRAL', 'MILIEU RELAYEUR', 'RELAYEUR',
    'وسط', 'وسط ميدان', 'لاعب وسط',
  ],
  AM: [
    'AM', 'CAM', 'MOC', 'ATTACKING MIDFIELDER', 'PLAYMAKER', 'MILIEU OFFENSIF',
    'MENEUR', 'MENEUR DE JEU', 'NUMERO 10', 'ENGANCHE',
    'صانع العاب', 'صانع الالعاب', 'وسط مهاجم',
  ],
  RM: ['RM', 'MDT', 'RIGHT MIDFIELDER', 'MILIEU DROIT', 'وسط ايمن'],
  LM: ['LM', 'MG', 'LEFT MIDFIELDER', 'MILIEU GAUCHE', 'وسط ايسر'],
  RW: [
    'RW', 'RWF', 'AD', 'AILIER DROIT', 'RIGHT WINGER', 'RIGHT WING', 'WINGER RIGHT',
    'جناح ايمن', 'جناح يمين',
  ],
  LW: [
    'LW', 'LWF', 'AG', 'AILIER GAUCHE', 'LEFT WINGER', 'LEFT WING', 'WINGER LEFT',
    'جناح ايسر', 'جناح يسار',
  ],
  CF: ['CF', 'SS', 'AC', 'SECOND STRIKER', 'CENTRE FORWARD', 'CENTER FORWARD', 'AVANT CENTRE', 'مهاجم ثان'],
  ST: [
    'ST', 'FW', 'CF9', 'A', 'ATT', 'ATTACKER', 'FORWARD', 'STRIKER', 'BUTEUR',
    'ATTAQUANT', 'AVANT', 'POINTE', 'NUMERO 9',
    'مهاجم', 'راس حربة', 'هجوم',
  ],
};

/** Shirt-number conventions, used only when a cell is nothing but a number. */
const BY_NUMBER: Record<string, string> = {
  '1': 'GK',
  '2': 'RB',
  '3': 'LB',
  '4': 'CB',
  '5': 'CB',
  '6': 'DM',
  '7': 'RW',
  '8': 'CM',
  '9': 'ST',
  '10': 'AM',
  '11': 'LW',
};

const LOOKUP = new Map<string, string>();
for (const [key, names] of Object.entries(ALIASES)) {
  for (const name of names) LOOKUP.set(normalizePosition(name), key);
}

export interface PositionMatch {
  key: string;
  /** How it was recognised, so the importer can say what it assumed. */
  via: 'key' | 'label' | 'alias' | 'number';
}

/**
 * Resolves one cell to a configured position key.
 *
 * `positions` is the org's own configuration, which always wins: a club that
 * renamed a position or added one of its own must not have it overruled by this
 * file's idea of football vocabulary.
 */
export function matchPosition(
  raw: string,
  positions: readonly PositionOption[] = [],
): PositionMatch | null {
  const value = normalizePosition(raw);
  if (!value) return null;

  for (const position of positions) {
    if (normalizePosition(position.key) === value) return { key: position.key, via: 'key' };
  }
  for (const position of positions) {
    if (normalizePosition(position.label) === value) return { key: position.key, via: 'label' };
  }

  const alias = LOOKUP.get(value);
  if (alias) {
    // Only offer a key the org actually has, when we know what it has.
    if (positions.length === 0 || positions.some((p) => p.key === alias)) {
      return { key: alias, via: 'alias' };
    }
  }

  if (/^\d{1,2}$/.test(value)) {
    const byNumber = BY_NUMBER[value];
    if (byNumber && (positions.length === 0 || positions.some((p) => p.key === byNumber))) {
      return { key: byNumber, via: 'number' };
    }
  }

  return null;
}

/** The key alone, for callers that do not care how it was found. */
export function resolvePositionKey(
  raw: string,
  positions: readonly PositionOption[] = [],
): string | null {
  return matchPosition(raw, positions)?.key ?? null;
}
